import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plane, Plus, Pencil, Trash2, X, ArrowLeft, Search, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  PICKER_AIRLINES,
  getProgramByValue,
  airlineTailLogoUrl,
} from '@/constants/loyaltyPrograms';
import { BRAND_PRIMARY_BTN_CLASS, REWARDS_SECTION_CARD } from '@/constants/brandColors';

// Same input chrome as the checkout PassengerForm so profile + checkout
// read as one surface.
const FIELD_BASE =
  'bg-white/[0.03] border-white/10 text-white placeholder:text-white/35 h-10 rounded-xl text-base md:text-sm font-normal px-3.5 transition-colors hover:bg-white/[0.05] focus-visible:ring-1 focus-visible:ring-[hsl(228,35%,58%)]/30 focus-visible:ring-offset-0 focus-visible:border-[hsl(228,35%,58%)]/30';

/**
 * Airline tail logo in a white chip (the proxied logo set is drawn for
 * light backgrounds). Falls back to the IATA code if the image 404s.
 */
export function TailLogo({ iata, size = 'md', className = '' }) {
  const [failed, setFailed] = useState(false);
  const sizes = {
    sm: 'w-8 h-8 p-1 rounded-lg',
    md: 'w-10 h-10 p-1.5 rounded-xl',
    lg: 'w-14 h-14 p-2 rounded-2xl',
  };
  return (
    <div
      className={cn(
        'bg-white/95 flex items-center justify-center overflow-hidden flex-shrink-0',
        'shadow-[inset_0_0_0_1px_rgba(0,0,0,0.06)]',
        sizes[size],
        className
      )}
    >
      {failed ? (
        <span className="text-[10px] font-medium text-slate-600 tracking-wide">{iata}</span>
      ) : (
        <img
          src={airlineTailLogoUrl(iata)}
          alt={`${iata} airline logo`}
          loading="lazy"
          className="w-full h-full object-contain"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}

/**
 * Add / edit modal. Adding is two steps — pick an airline from the logo
 * grid, then enter the member number. Editing jumps straight to the
 * number step.
 */
function AirlineLoyaltyModal({ mode, account, existingKeys, onSave, onClose }) {
  const editingProgram = mode === 'edit' ? getProgramByValue(account.program_key) : null;
  const [step, setStep] = useState(mode === 'edit' ? 'number' : 'pick');
  const [selectedProgram, setSelectedProgram] = useState(editingProgram);
  const [accountNumber, setAccountNumber] = useState(mode === 'edit' ? account.account_number : '');
  const [search, setSearch] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const filteredAirlines = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return PICKER_AIRLINES;
    return PICKER_AIRLINES.filter(
      (p) =>
        p.airline.toLowerCase().includes(q) ||
        p.label.toLowerCase().includes(q) ||
        p.iata.some((code) => code.toLowerCase() === q)
    );
  }, [search]);

  const handlePick = (program) => {
    if (existingKeys.has(program.value)) return;
    setSelectedProgram(program);
    setError(null);
    setStep('number');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProgram || !accountNumber.trim() || isSaving) return;
    setIsSaving(true);
    setError(null);
    try {
      await onSave({
        program_key: selectedProgram.value,
        airline_iata: selectedProgram.iata[0],
        account_number: accountNumber,
      });
      onClose();
    } catch (err) {
      console.error('Failed to save loyalty account:', err);
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Portal to <body>: the wallet card's backdrop-blur makes it the
  // containing block for position:fixed, and its overflow-hidden would
  // clip the modal to the card bounds.
  return createPortal(
    <motion.div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-20 sm:pt-24 overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        className="relative bg-[#070b18] border border-white/10 rounded-3xl w-full max-w-lg my-8 overflow-hidden shadow-[0_24px_80px_rgba(0,0,0,0.6)]"
        initial={{ scale: 0.95, opacity: 0, y: 8 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 8 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5 min-w-0">
            {step === 'number' && mode !== 'edit' && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => { setStep('pick'); setError(null); }}
                className="h-8 w-8 rounded-full text-white/55 hover:text-white hover:bg-white/[0.06] flex-shrink-0 -ml-1"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(228,35%,58%)]/85 font-medium">
                Frequent flyer
              </p>
              <h3 className="text-white text-lg font-light tracking-tight truncate">
                {step === 'pick' ? 'Pick your airline' : (mode === 'edit' ? 'Edit member number' : 'Add member number')}
              </h3>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 rounded-full text-white/55 hover:text-white hover:bg-white/[0.06] flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {step === 'pick' ? (
          <div className="p-5 sm:p-6 space-y-4">
            <div className="relative">
              <Search className="w-4 h-4 text-white/35 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search airlines…"
                autoFocus
                className={cn(FIELD_BASE, 'pl-10')}
              />
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5 max-h-[46vh] overflow-y-auto pr-1 -mr-1">
              {filteredAirlines.map((program) => {
                const added = existingKeys.has(program.value);
                return (
                  <button
                    key={program.value}
                    type="button"
                    onClick={() => handlePick(program)}
                    disabled={added}
                    className={cn(
                      'flex flex-col items-center gap-2 rounded-2xl border px-2 py-3.5 transition-all duration-150',
                      added
                        ? 'border-brand-800/40 bg-brand-900/25 opacity-45 cursor-default'
                        : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-[hsl(228,35%,58%)]/40 hover:-translate-y-0.5'
                    )}
                  >
                    <TailLogo iata={program.iata[0]} size="lg" />
                    <span className="text-[11px] text-white/75 font-normal leading-tight text-center">
                      {program.airline}
                    </span>
                    {added && (
                      <span className="text-[9px] uppercase tracking-[0.14em] text-brand-400 font-normal flex items-center gap-1">
                        <Check className="w-2.5 h-2.5" /> Added
                      </span>
                    )}
                  </button>
                );
              })}
              {filteredAirlines.length === 0 && (
                <p className="col-span-full text-center text-white/60 text-sm font-normal py-8">
                  No airlines match “{search}”.
                </p>
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-5">
            <div className="flex items-center gap-4 rounded-2xl border border-brand-800/40 bg-brand-900/20 px-4 py-4">
              <TailLogo iata={selectedProgram.iata[0]} size="lg" />
              <div className="min-w-0">
                <p className="text-white text-[15px] font-normal tracking-tight truncate">{selectedProgram.airline}</p>
                <p className="text-[hsl(228,30%,72%)] text-[12px] font-normal truncate">{selectedProgram.label}</p>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-normal text-white/55 tracking-wide">Member number</Label>
              <Input
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                placeholder={`e.g. ${selectedProgram.iata[0]}1234567`}
                autoFocus
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="done"
                onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(e); }}
                className={cn(FIELD_BASE, 'tracking-wider tabular-nums')}
              />
              <p className="text-white/60 text-[11px] font-normal pt-0.5">
                We’ll apply this automatically at checkout whenever you book {selectedProgram.airline}.
              </p>
            </div>
            {error && (
              <div className="p-3 rounded-xl bg-red-500/15 border border-red-500/25 text-red-300 text-sm font-normal">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-2.5 pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSaving}
                className="rounded-full h-10 px-5 bg-white/[0.03] border-white/15 text-white/80 hover:bg-white/[0.08] hover:text-white font-normal"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSaving || !accountNumber.trim()}
                className={cn('rounded-full h-10 px-6', BRAND_PRIMARY_BTN_CLASS)}
              >
                {isSaving ? (
                  <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Saving…</span>
                ) : (
                  <span className="flex items-center gap-2"><Check className="w-4 h-4" /> Save</span>
                )}
              </Button>
            </div>
          </form>
        )}
      </motion.div>
    </motion.div>,
    document.body
  );
}

/**
 * "Frequent Flyer Programs" wallet card on My Profile. One tile per linked
 * airline; numbers auto-apply at checkout when the itinerary's marketing
 * carrier matches (PassengerForm.jsx).
 */
export default function FrequentFlyerPrograms({ accounts, onSave, onDelete }) {
  const [modalState, setModalState] = useState(null); // null | {mode:'add'} | {mode:'edit', account}
  const [deletingId, setDeletingId] = useState(null);

  const existingKeys = useMemo(
    () => new Set(accounts.map((a) => a.program_key)),
    [accounts]
  );

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await onDelete(id);
    } catch (err) {
      console.error('Failed to delete loyalty account:', err);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className={REWARDS_SECTION_CARD}>
      <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-5 border-b border-white/[0.06]">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-[hsl(228,40%,38%)]/[0.16] border border-[hsl(228,35%,58%)]/20 flex items-center justify-center flex-shrink-0">
            <Plane className="w-4 h-4 text-[hsl(228,30%,72%)]" />
          </div>
          <div className="min-w-0">
            <h3 className="text-white text-[15px] font-normal tracking-tight">Frequent Flyer Programs</h3>
            <p className="text-white/60 text-[11px] font-normal">Auto-applied at checkout for matching airlines</p>
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={() => setModalState({ mode: 'add' })}
          className={cn('rounded-full h-9 px-4 text-xs flex-shrink-0', BRAND_PRIMARY_BTN_CLASS)}
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add airline
        </Button>
      </div>

      <div className="p-5 sm:p-6">
        {accounts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <AnimatePresence initial={false}>
              {accounts.map((account) => {
                const program = getProgramByValue(account.program_key);
                return (
                  <motion.div
                    key={account.id}
                    layout
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.97 }}
                    className="group flex items-center gap-3.5 rounded-2xl border border-brand-800/40 bg-brand-900/20 px-4 py-3.5 transition-colors hover:border-brand-700/45"
                  >
                    <TailLogo iata={account.airline_iata} size="md" />
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm font-normal tracking-tight truncate">
                        {program?.airline || account.airline_iata}
                      </p>
                      <p className="text-white/40 text-[11px] font-normal truncate">
                        {program?.label || account.program_key}
                      </p>
                      <p className="text-[hsl(228,30%,72%)] text-[12px] font-normal tabular-nums tracking-wider mt-0.5 truncate">
                        {account.account_number}
                      </p>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setModalState({ mode: 'edit', account })}
                        className="h-8 w-8 rounded-full text-white/45 hover:text-white hover:bg-white/[0.08]"
                        title="Edit member number"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(account.id)}
                        disabled={deletingId === account.id}
                        className="h-8 w-8 rounded-full text-red-400/70 hover:text-red-300 hover:bg-red-500/10"
                        title="Remove airline"
                      >
                        {deletingId === account.id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Trash2 className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        ) : (
          <div className="text-center py-8 px-4">
            <div className="flex justify-center items-center gap-2.5 mb-5">
              {PICKER_AIRLINES.slice(0, 6).map((p) => (
                <TailLogo key={p.value} iata={p.iata[0]} size="sm" className="opacity-80" />
              ))}
            </div>
            <p className="text-white text-sm font-normal mb-1.5">No airlines linked yet</p>
            <p className="text-white/60 text-[12.5px] font-normal max-w-sm mx-auto leading-relaxed">
              Add your frequent flyer numbers once — the right one is applied
              automatically at checkout based on the airline you’re booking.
            </p>
            <Button
              type="button"
              onClick={() => setModalState({ mode: 'add' })}
              className={cn('mt-5 rounded-full h-10 px-6 text-[13px]', BRAND_PRIMARY_BTN_CLASS)}
            >
              <Plus className="w-4 h-4 mr-2" /> Add your first airline
            </Button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {modalState && (
          <AirlineLoyaltyModal
            mode={modalState.mode}
            account={modalState.account}
            existingKeys={existingKeys}
            onSave={onSave}
            onClose={() => setModalState(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
